import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    IconButton,
    Alert,
    Snackbar,
    CircularProgress,
    Typography,
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
} from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
    procurementService,
    Supplier,
    UpdatePurchaseRequest,
} from "../../services/procurementService";
import { productService } from "../../services/productService";
import { Product } from "../../types";
import {
    PurchaseOrderForm,
    PurchaseOrderItem,
    PurchaseOrderFormData,
    calculatePurchaseOrderTotals,
    hasDuplicateProducts,
} from "../../components/procurement/PurchaseOrderForm";
import { compareByProductName } from "../../utils/procurementSort";

const EditPurchaseOrder: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const backTo =
        (location.state as { from?: string } | null)?.from ||
        "/procurement/purchases";
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
        null,
    );
    const [expectedDeliveryDate, setExpectedDeliveryDate] =
        useState<Date | null>(null);
    const [items, setItems] = useState<PurchaseOrderItem[]>([]);

    const [formData, setFormData] = useState<PurchaseOrderFormData>({
        reference_number: "",
        payment_terms: "",
        shipping_cost: 0,
        discount_amount: 0,
        notes: "",
    });
    // tax_amount has no UI (tax isn't used anywhere in this business) — we
    // just carry forward whatever was already on the purchase so editing
    // doesn't silently zero out a legacy value.
    const [taxAmount, setTaxAmount] = useState(0);

    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({
        open: false,
        message: "",
        severity: "success",
    });

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [purchase, suppliersData, productsData] = await Promise.all([
                procurementService.getPurchase(id!),
                procurementService.getSuppliers({
                    active_only: true,
                    limit: 1000,
                }),
                productService.getProducts({ active_only: true, limit: 1000 }),
            ]);

            setSuppliers(suppliersData);
            setProducts(productsData.data || []);

            // Set form data from purchase. shipping_cost/discount_amount come
            // back from the API as Decimal-serialized strings (e.g. "0.00")
            // — coerce to real numbers, otherwise arithmetic downstream
            // silently does string concatenation instead of addition.
            setFormData({
                reference_number: purchase.reference_number || "",
                payment_terms: purchase.payment_terms || "",
                shipping_cost: Number(purchase.shipping_cost) || 0,
                discount_amount: Number(purchase.discount_amount) || 0,
                notes: purchase.notes || "",
            });

            setTaxAmount(Number(purchase.tax_amount) || 0);

            // Set supplier
            if (purchase.supplier) {
                setSelectedSupplier(purchase.supplier);
            }

            // Set delivery date
            if (purchase.expected_delivery_date) {
                setExpectedDeliveryDate(
                    new Date(purchase.expected_delivery_date),
                );
            }

            // Set items
            if (purchase.items) {
                const mappedItems: PurchaseOrderItem[] = purchase.items.map(
                    (item) => ({
                        product_id: item.product_id,
                        product: item.product as Product | undefined,
                        quantity_ordered: item.quantity_ordered,
                        unit_cost: Number(item.unit_cost),
                        total_cost: Number(item.total_cost),
                        is_gift: item.is_gift || false,
                        bonus_quantity: item.bonus_quantity || 0,
                        quality_check_required:
                            item.quality_check_required || false,
                        requested_batch_info: item.requested_batch_info || "",
                        notes: item.notes || "",
                    }),
                );
                // Sorted once, here, when the edit screen first loads —
                // not on every render inside PurchaseOrderForm, which
                // would make a row jump position mid-edit as the user
                // works. Whatever order the items end up in during this
                // session is left alone.
                mappedItems.sort(compareByProductName);
                setItems(mappedItems);
            }
        } catch (error) {
            console.error("Error loading data:", error);
            showSnackbar("Failed to load purchase order", "error");
        } finally {
            setLoading(false);
        }
    };

    const showSnackbar = (message: string, severity: "success" | "error") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSubmit = async () => {
        // Validation
        if (!selectedSupplier) {
            showSnackbar("Please select a supplier", "error");
            return;
        }

        if (items.length === 0) {
            showSnackbar("Please add at least one item", "error");
            return;
        }

        const invalidItems = items.filter(
            (item) =>
                !item.product_id ||
                item.quantity_ordered <= 0 ||
                (item.unit_cost <= 0 && !item.is_gift),
        );
        if (invalidItems.length > 0) {
            showSnackbar(
                "Please fill in all required fields for all items",
                "error",
            );
            return;
        }

        if (hasDuplicateProducts(items)) {
            showSnackbar(
                "The same product appears on multiple lines — merge them into one line (use Bonus Qty for free extra units) before saving.",
                "error",
            );
            return;
        }

        try {
            setSaving(true);

            // total_amount is the pure item subtotal — the server derives
            // net_payable (what's actually owed) as
            // total_amount - discount_amount + tax_amount + shipping_cost,
            // so shipping/discount must NOT be folded in here too.
            const { subtotal } = calculatePurchaseOrderTotals(items, formData);

            const updateData: UpdatePurchaseRequest = {
                reference_number: formData.reference_number || undefined,
                payment_terms: formData.payment_terms || undefined,
                shipping_cost: formData.shipping_cost
                    ? Number(Number(formData.shipping_cost).toFixed(2))
                    : undefined,
                expected_delivery_date:
                    expectedDeliveryDate?.toISOString() || undefined,
                notes: formData.notes || undefined,
                total_amount: Number(subtotal.toFixed(2)),
                discount_amount: formData.discount_amount
                    ? Number(Number(formData.discount_amount).toFixed(2))
                    : undefined,
                tax_amount: taxAmount
                    ? Number(Number(taxAmount).toFixed(2))
                    : undefined,
                items: items.map((item) => ({
                    product_id: item.product_id,
                    quantity_ordered: item.quantity_ordered,
                    unit_cost: item.is_gift
                        ? 0
                        : Number(Number(item.unit_cost).toFixed(2)),
                    total_cost: item.is_gift
                        ? 0
                        : Number(Number(item.total_cost).toFixed(2)),
                    is_gift: item.is_gift || false,
                    bonus_quantity: item.is_gift
                        ? 0
                        : item.bonus_quantity || 0,
                    quality_check_required: item.quality_check_required,
                    requested_batch_info: item.requested_batch_info,
                    notes: item.notes,
                })),
            };

            await procurementService.updatePurchase(id!, updateData);
            showSnackbar("Purchase order updated successfully", "success");

            setTimeout(() => {
                navigate(backTo);
            }, 1500);
        } catch (error) {
            console.error("Error updating purchase order:", error);
            showSnackbar("Failed to update purchase order", "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "400px",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 0, sm: 3 } }}>
            {/* Header */}
            <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                mb={3}
                sx={{ flexWrap: "wrap", gap: 2 }}
            >
                <Box display="flex" alignItems="center">
                    <IconButton
                        onClick={() => navigate(backTo)}
                        sx={{ mr: 1 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontSize: { xs: "1.35rem", sm: "2.125rem" } }}
                    >
                        Edit Purchase Order
                    </Typography>
                </Box>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSubmit}
                    disabled={saving || !selectedSupplier || items.length === 0}
                >
                    {saving ? <CircularProgress size={20} /> : "Save Changes"}
                </Button>
            </Box>

            {/* Reusable Form Component */}
            <PurchaseOrderForm
                suppliers={suppliers}
                products={products}
                selectedSupplier={selectedSupplier}
                setSelectedSupplier={setSelectedSupplier}
                expectedDeliveryDate={expectedDeliveryDate}
                setExpectedDeliveryDate={setExpectedDeliveryDate}
                formData={formData}
                setFormData={setFormData}
                items={items}
                setItems={setItems}
                mode="edit"
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EditPurchaseOrder;
